import { useState } from 'react'
import { Button, Input, Select, Space, Tag, Typography, Drawer } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { auditApi, type AuditLog } from '@/features/audit/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'

const ACTION_OPTIONS = ['CREATE', 'UPDATE', 'DEACTIVATE', 'ACTIVATE', 'LOGIN', 'LOGOUT', 'ASSIGN_ROLES', 'ASSIGN_PERMISSIONS']
  .map((a) => ({ label: a, value: a }))

const ACTION_COLOR: Record<string, string> = {
  CREATE: 'green',
  UPDATE: 'blue',
  DEACTIVATE: 'red',
  ACTIVATE: 'green',
  LOGIN: 'cyan',
  LOGOUT: 'default',
  ASSIGN_ROLES: 'purple',
  ASSIGN_PERMISSIONS: 'purple',
}

export default function AuditLogPage() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [entityFilter, setEntityFilter] = useState('')
  const [actionFilter, setActionFilter] = useState<string | undefined>()
  const [detailRecord, setDetailRecord] = useState<AuditLog | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', page, pageSize, entityFilter, actionFilter],
    queryFn: () =>
      auditApi.list({
        page,
        page_size: pageSize,
        entity_name: entityFilter || undefined,
        action: actionFilter,
      }),
  })

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
      width: 180,
    },
    { title: 'Entity', dataIndex: 'entity_name', key: 'entity_name' },
    {
      title: 'Entity ID',
      dataIndex: 'entity_id',
      key: 'entity_id',
      render: (v: string) => (
        <Typography.Text copyable style={{ fontSize: 12 }}>
          {v.slice(0, 8)}…
        </Typography.Text>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (v: string) => <Tag color={ACTION_COLOR[v] ?? 'default'}>{v}</Tag>,
    },
    { title: 'Performed By', dataIndex: 'performed_by_username', key: 'performed_by_username', render: (v?: string) => v ?? '—' },
    { title: 'IP Address', dataIndex: 'ip_address', key: 'ip_address', render: (v?: string) => v ?? '—' },
    {
      title: 'Details',
      key: 'details',
      render: (_: unknown, record: AuditLog) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => setDetailRecord(record)}>
          View
        </Button>
      ),
    },
  ]

  return (
    <div>
      <PageHeader title="Audit Log" subtitle="Read-only record of all system changes" />

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="Filter by entity name…"
          allowClear
          onChange={(e) => { setEntityFilter(e.target.value); setPage(1) }}
          style={{ width: 200 }}
        />
        <Select
          allowClear
          placeholder="Filter by action"
          options={ACTION_OPTIONS}
          onChange={(v) => { setActionFilter(v); setPage(1) }}
          style={{ width: 180 }}
        />
      </Space>

      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={data?.items}
        loading={isLoading}
        total={data?.total}
        page={page}
        pageSize={pageSize}
        onPageChange={(p, ps) => { setPage(p); setPageSize(ps) }}
      />

      {/* Detail drawer — shows before/after JSON */}
      <Drawer
        title="Audit Detail"
        open={!!detailRecord}
        onClose={() => setDetailRecord(null)}
        width={560}
      >
        {detailRecord && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Typography.Text strong>Entity: </Typography.Text>
              <Typography.Text>{detailRecord.entity_name} / {detailRecord.entity_id}</Typography.Text>
            </div>
            <div>
              <Typography.Text strong>Action: </Typography.Text>
              <Tag color={ACTION_COLOR[detailRecord.action] ?? 'default'}>{detailRecord.action}</Tag>
            </div>
            <div>
              <Typography.Text strong>Performed By: </Typography.Text>
              <Typography.Text>{detailRecord.performed_by_username ?? detailRecord.performed_by}</Typography.Text>
            </div>
            <div>
              <Typography.Text strong>Timestamp: </Typography.Text>
              <Typography.Text>{dayjs(detailRecord.created_at).format('YYYY-MM-DD HH:mm:ss')}</Typography.Text>
            </div>
            {detailRecord.before_data && (
              <div>
                <Typography.Text strong>Before:</Typography.Text>
                <pre
                  style={{
                    background: '#f5f5f5',
                    padding: 12,
                    borderRadius: 4,
                    fontSize: 12,
                    overflow: 'auto',
                    maxHeight: 240,
                  }}
                >
                  {JSON.stringify(detailRecord.before_data, null, 2)}
                </pre>
              </div>
            )}
            {detailRecord.after_data && (
              <div>
                <Typography.Text strong>After:</Typography.Text>
                <pre
                  style={{
                    background: '#f5f5f5',
                    padding: 12,
                    borderRadius: 4,
                    fontSize: 12,
                    overflow: 'auto',
                    maxHeight: 240,
                  }}
                >
                  {JSON.stringify(detailRecord.after_data, null, 2)}
                </pre>
              </div>
            )}
          </Space>
        )}
      </Drawer>
    </div>
  )
}
